import type { RequestDetailViewState } from "./request-detail-view-types";
import { isPreviewableAttachment } from "./request-detail-presentation";
import { Eye, FileText, Paperclip, Pencil, Trash2 } from "lucide-react";

type Props = Pick<
  RequestDetailViewState,
  | "detail"
  | "setPreviewAttachment"
  | "openOfferDocumentsId"
  | "setOpenOfferDocumentsId"
  | "savingOffer"
  | "pendingOfferRemovalId"
  | "setPendingOfferRemovalId"
  | "removingOfferId"
  | "openOfferEditor"
  | "removeOffer"
  | "comparison"
  | "getOfferAttachments"
  | "canManageOffers"
>;
export function RequestOfferList({
  detail,
  setPreviewAttachment,
  openOfferDocumentsId,
  setOpenOfferDocumentsId,
  savingOffer,
  pendingOfferRemovalId,
  setPendingOfferRemovalId,
  removingOfferId,
  openOfferEditor,
  removeOffer,
  comparison,
  getOfferAttachments,
  canManageOffers,
}: Props) {
  return detail.offers.length ? (
                <div className="detail-offer-list">
                  <div className="detail-offer-list-header" aria-hidden="true">
                    <span>Tedarikçi / teklif</span>
                    <span>Toplam bedel</span>
                    <span>Ödeme vadesi</span>
                    <span>Teslimat</span>
                    {canManageOffers ? <span>İşlemler</span> : null}
                  </div>
                  {detail.offers.map((offer) => (
                    <article key={offer.id} className="detail-offer">
                      <div>
                        <strong>{offer.supplier}</strong>
                        <small>{offer.offer_no}</small>
                        {getOfferAttachments(offer).length ? (
                          <div className="detail-offer-documents">
                            <button
                              type="button"
                              className="detail-offer-documents-trigger"
                              aria-expanded={openOfferDocumentsId === offer.id}
                              onClick={() =>
                                setOpenOfferDocumentsId((current) =>
                                  current === offer.id ? null : offer.id,
                                )
                              }
                            >
                              <Paperclip size={13} aria-hidden="true" />
                              {getOfferAttachments(offer).length} belge
                            </button>
                            {openOfferDocumentsId === offer.id ? (
                              <div
                                className="detail-offer-documents-menu"
                                role="menu"
                              >
                                <strong>Teklif belgeleri</strong>
                                {getOfferAttachments(offer).map(
                                  (attachment) => (
                                    <button
                                      type="button"
                                      role="menuitem"
                                      key={attachment.id}
                                      title={attachment.original_name}
                                      disabled={
                                        !isPreviewableAttachment(attachment)
                                      }
                                      onClick={() => {
                                        setOpenOfferDocumentsId(null);
                                        setPreviewAttachment(attachment);
                                      }}
                                    >
                                      <FileText size={14} aria-hidden="true" />
                                      <span>{attachment.original_name}</span>
                                      {isPreviewableAttachment(attachment) ? (
                                        <Eye size={14} aria-hidden="true" />
                                      ) : null}
                                    </button>
                                  ),
                                )}
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                      <strong
                        className={`detail-offer-amount${comparison?.lowest.id === offer.id ? " is-best-metric" : ""}`}
                      >
                        {offer.formattedAmount}
                        {comparison?.lowest.id === offer.id ? (
                          <em className="approval-offer-best">En düşük</em>
                        ) : null}
                      </strong>
                      <span
                        className={
                          comparison?.longestPayment.id === offer.id
                            ? "is-best-metric"
                            : ""
                        }
                      >
                        <b>{offer.payment_days} gün</b>
                        {comparison?.longestPayment.id === offer.id ? (
                          <em className="approval-offer-best">En uzun vade</em>
                        ) : null}
                      </span>
                      <span
                        className={
                          comparison?.fastest.id === offer.id
                            ? "is-best-metric"
                            : ""
                        }
                      >
                        <b>{offer.delivery_days} gün</b>
                        {comparison?.fastest.id === offer.id ? (
                          <em className="approval-offer-best">En hızlı</em>
                        ) : null}
                      </span>
                      {canManageOffers ? (
                        <div className="detail-offer-actions">
                          <button
                            type="button"
                            aria-label={`${offer.offer_no} teklifini düzenle`}
                            title="Teklifi düzenle"
                            onClick={() => void openOfferEditor(offer)}
                          >
                            <Pencil size={15} />
                          </button>
                          <button
                            type="button"
                            className="is-danger"
                            aria-label={`${offer.offer_no} teklifini kaldır`}
                            title="Teklifi kaldır"
                            onClick={() => setPendingOfferRemovalId(offer.id)}
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      ) : null}
                      {canManageOffers && pendingOfferRemovalId === offer.id ? (
                        <div
                          className="detail-offer-removal"
                          role="alertdialog"
                          aria-label="Teklifi kaldırma onayı"
                        >
                          <span>
                            <strong>{offer.supplier}</strong> teklifini
                            kaldırmak istediğinize emin misiniz? Kayıt denetim
                            geçmişinde korunur.
                          </span>
                          <button
                            type="button"
                            className="secondary-button"
                            onClick={() => setPendingOfferRemovalId(null)}
                          >
                            Vazgeç
                          </button>
                          <button
                            type="button"
                            className="reject-button"
                            disabled={Boolean(removingOfferId) || savingOffer}
                            onClick={() => void removeOffer(offer)}
                          >
                            {removingOfferId === offer.id
                              ? "Kaldırılıyor"
                              : "Teklifi kaldır"}
                          </button>
                        </div>
                      ) : null}
                    </article>
                  ))}
                </div>
              ) : (
                <p className="detail-empty">
                  Bu talep için henüz teklif kaydedilmedi.
                </p>
              );
}
